import {
	ChangeDetectionStrategy,
	Component,
	input,
	output,
	computed,
	inject,
} from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CheckboxComponent } from 'src/app/ui/checkbox/checkbox.component';
import {
	DropdownComponent,
	DropdownItem,
} from 'src/app/ui/dropdown/dropdown.component';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { TablerIconComponent } from 'angular-tabler-icons';
import { IntakeResponsesRowComponent } from '../intake-responses-row/intake-responses-row.component';
import {
	FormOption,
	IntakeResponseRow,
	IntakeResponseSection,
} from '../../view-models/models';
import { FieldResponseStatus } from '@models/client-intake';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

@Component({
	selector: 'app-intake-responses-table',
	templateUrl: './intake-responses-table.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		CheckboxComponent,
		DropdownComponent,
		ButtonComponent,
		TablerIconComponent,
		IntakeResponsesRowComponent,
		MatTooltipModule,
		NgClass,
	],
})
export class IntakeResponsesTableComponent {
	sections = input<IntakeResponseSection[]>([]);

	formOptions = input<FormOption[]>([]);
	selectedFormId = input<string | null>(null);

	checkboxStates = input<Record<string, Record<string, boolean>>>({});
	allItemsSelected = input<boolean>(false);

	showSelectAllCheckbox = input<boolean>(false);
	showMatterDetailsColumn = input<boolean>(true);
	showStatusColumn = input<boolean>(false);

	formSelected = output<string>();

	rowToggled = output<{ row: IntakeResponseRow; checked: boolean }>();
	selectAllToggled = output<boolean>();

	analyticsService = inject(AnalyticsService);

	collapsedSections: { [key: string]: boolean } = {};

	dropdownItems = computed(() => {
		return this.formOptions().map((form) => ({
			id: form.formId,
			title: {
				label: `${form.formName} (${form.responseCount} responses)`,
			},
			action: () => this.onFormSelected(form.formId),
		}));
	});
	fieldResponseStatus = FieldResponseStatus;

	goToPreviousForm(): void {
		const currentFormId = this.selectedFormId();

		const currentFormIndex = this.formOptions().findIndex(
			(x) => x.formId === currentFormId,
		);

		const previousFormIndex =
			currentFormIndex - 1 >= 0 ? currentFormIndex - 1 : 0;

		const previousFormId = this.formOptions()[previousFormIndex]?.formId;

		if (previousFormId) {
			this.onFormSelected(previousFormId);
		}
	}

	goToNextForm(): void {
		const currentFormId = this.selectedFormId();

		const currentFormIndex = this.formOptions().findIndex(
			(x) => x.formId === currentFormId,
		);

		const nextFormIndex =
			currentFormIndex + 1 < this.formOptions().length
				? currentFormIndex + 1
				: currentFormIndex;

		const nextFormId = this.formOptions()[nextFormIndex]?.formId;

		if (nextFormId) {
			this.onFormSelected(nextFormId);
		}
	}

	toggleCollapsed(sectionTitle: string): void {
		this.collapsedSections[sectionTitle] =
			!this.collapsedSections[sectionTitle];
	}

	isSectionCollapsed(sectionTitle: string): boolean {
		return this.collapsedSections[sectionTitle] || false;
	}

	onFormSelected(formId: string): void {
		this.formSelected.emit(formId);
	}

	onDropdownItemSelected(item: DropdownItem): void {
		this.analyticsService.logEvent(
			AnalyticsEvent.INTAKE_PARTIES_REVIEW_RESPONSES_NAVIGATION,
		);

		if (item.action) {
			item.action();
		}
	}

	getSelectedFormName(): string {
		const selectedForm = this.formOptions().find(
			(form) => form.formId === this.selectedFormId(),
		);
		return selectedForm?.formName || 'Select Form';
	}

	onRowToggled(row: IntakeResponseRow, checked: boolean): void {
		this.rowToggled.emit({ row, checked });
	}

	onSelectAllClick(event: Event): void {
		event.preventDefault();
		event.stopPropagation();

		const shouldSelectAll = !this.areAllItemsSelected();
		this.selectAllToggled.emit(shouldSelectAll);
	}

	isRowSelected(row: IntakeResponseRow): boolean {
		const currentFormId = this.selectedFormId();
		if (!currentFormId) return false;

		return this.checkboxStates()[currentFormId]?.[row.questionId] || false;
	}

	areAllItemsSelected(): boolean {
		return this.allItemsSelected();
	}

	clientResponseMatchesMatterDetails(row: IntakeResponseRow): boolean {
		const { clientResponse, matterDetails } = row;

		// clientResponse represents an uploaded file if it is a file metadata object containing a fullPath property
		const isFile: boolean =
			clientResponse !== null &&
			typeof clientResponse === 'object' &&
			Object.prototype.hasOwnProperty.call(clientResponse, 'fullPath');

		if (isFile) {
			return clientResponse['fullPath'] === matterDetails;
		} else {
			return clientResponse === matterDetails;
		}
	}
}
